import {selectUserId} from '@selectors/authSelector';
import {useSelector} from 'react-redux';
import {selectOrganisationContextId} from '@selectors/appSelector';
import {selectOrganisationTheme} from '@selectors/organisationSelector';
import { buildCustomerCallbackReportingFilter } from '@api/callback';
import CustomerDashboard from '@components/CustomerDashboard/CustomerDashboard';
import {
	useExportQAMutation,
	useGetAskedQuestionsQuery,
	useGetAverageDocumentsQuery,
	useGetAverageQuestionsQuery,
	useGetBehaviouralUsageQuery,
	useGetFeedbackRatingsQuery,
	useGetInsightsUsageQuery,
	useGetOrganisationUsersQuery,
	useGetTotalTransactionsQuery,
	useGetUserBehavioursQuery,
	useGetCallbackReportingQuery,
	useGetCustomerCreditDashboardQuery,
} from '@api/dashboards';
import {useGetCurrentUserQuery} from '@api/users';
import {useMemo, useState} from 'react';
import {DateFilter} from '@shared/models/dashboard';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const Home = () => {
	const userId = useSelector(selectUserId);
	const organisationId = useSelector(selectOrganisationContextId);
	const theme = useSelector(selectOrganisationTheme(organisationId));
	const { t } = useTranslation();

	const [filterDate, setFilters] = useState<DateFilter>({
		start: dayjs().utc().subtract(7, 'day').startOf('day'),
		end: dayjs().utc().startOf('day'),
	});

	const queryArgs = useMemo(
		() => ({
			startDate: dayjs(filterDate.start).utc().startOf('day').toISOString(),
			endDate: dayjs(filterDate.end).utc().endOf('day').toISOString(),
			organisationId: organisationId ?? '',
		}),
		[filterDate.start, filterDate.end, organisationId],
	);
	const callbackReportingArgs = useMemo(
		() =>
			organisationId
				? buildCustomerCallbackReportingFilter({
						organisationId,
						startDate: queryArgs.startDate,
						endDate: queryArgs.endDate,
				  })
				: undefined,
		[organisationId, queryArgs.endDate, queryArgs.startDate],
	);

	const {data: user} = useGetCurrentUserQuery(
		{organisationId: organisationId!},
		{skip: !userId || !organisationId},
	);
	const {
		data: askedQuestions,
		isFetching: isFetchingAskedQuestions,
		isError: isErrorAskedQuestions,
	} = useGetAskedQuestionsQuery(queryArgs, {
		skip: !organisationId,
		refetchOnMountOrArgChange: true,
	});
	const {
		data: averageQuestions,
		isFetching: isFetchingAverageQuestions,
		isError: isErrorAverageQuestions,
	} = useGetAverageQuestionsQuery(queryArgs, {
		skip: !organisationId,
		refetchOnMountOrArgChange: true,
	});
	const {
		data: organisationUsers,
		isFetching: isFetchingOrganisationUsers,
		isError: isErrorOrganisationUsers,
	} = useGetOrganisationUsersQuery(queryArgs, {
		skip: !organisationId,
		refetchOnMountOrArgChange: true,
	});
	const {
		data: totalTransactions,
		isFetching: isFetchingTotalTransactions,
		isError: isErrorTotalTransactions,
	} = useGetTotalTransactionsQuery(queryArgs, {
		skip: !organisationId,
		refetchOnMountOrArgChange: true,
	});
	const {
		data: insightUsage,
		isFetching: isFetchingInsightUsage,
		isError: isErrorInsightUsage,
	} = useGetInsightsUsageQuery(queryArgs, {
		skip: !organisationId,
		refetchOnMountOrArgChange: true,
	});
	const {
		data: behaviouralUsage,
		isFetching: isFetchingBehaviouralUsage,
		isError: isErrorBehaviouralUsage,
	} = useGetBehaviouralUsageQuery(queryArgs, {
		skip: !organisationId,
		refetchOnMountOrArgChange: true,
	});
	const {
		data: feedbackRatings,
		isFetching: isFetchingFeedbackRatings,
		isError: isErrorFeedbackRatings,
	} = useGetFeedbackRatingsQuery(queryArgs, {
		skip: !organisationId,
		refetchOnMountOrArgChange: true,
	});
	const {
		data: userBehaviours,
		isFetching: isFetchingUserBehaviours,
		isError: isErrorUserBehaviours,
	} = useGetUserBehavioursQuery(queryArgs, {
		skip: !organisationId,
		refetchOnMountOrArgChange: true,
	});
	const {
		data: averageDocs,
		isFetching: isFetchingAverageDocs,
		isError: isErrorAverageDocs,
	} = useGetAverageDocumentsQuery(queryArgs, {
		skip: !organisationId,
		refetchOnMountOrArgChange: true,
	});
	const {
		data: creditDashboard,
		isFetching: isFetchingCreditDashboard,
		isError: isErrorCreditDashboard,
	} = useGetCustomerCreditDashboardQuery(queryArgs, {
		skip: !organisationId,
		refetchOnMountOrArgChange: true,
	});
	const {
		data: callbackReporting,
		isFetching: isFetchingCallbackReporting,
		isError: isErrorCallbackReporting,
	} = useGetCallbackReportingQuery(callbackReportingArgs!, {
		skip: !callbackReportingArgs,
		refetchOnMountOrArgChange: true,
	});
	const [download, {isLoading: isDownloadingData}] = useExportQAMutation();

	if (!!!userId || !!!organisationId) {
		return null;
	}

	return (
		<CustomerDashboard
			queryArgs={queryArgs}
			setFilters={setFilters}
			theme={theme}
			filterDate={filterDate}
			organisationId={organisationId}
			userRequest={{data: user}}
			askedQuestionsRequest={{
				data: askedQuestions,
				isFetching: isFetchingAskedQuestions,
				isError: isErrorAskedQuestions,
			}}
			averageQuestionsRequest={{
				data: averageQuestions,
				isFetching: isFetchingAverageQuestions,
				isError: isErrorAverageQuestions,
			}}
			organisationUsersRequest={{
				data: organisationUsers,
				isFetching: isFetchingOrganisationUsers,
				isError: isErrorOrganisationUsers,
			}}
			totalTransactionsRequest={{
				data: totalTransactions,
				isFetching: isFetchingTotalTransactions,
				isError: isErrorTotalTransactions,
			}}
			insightsUsageRequest={{
				data: insightUsage,
				isFetching: isFetchingInsightUsage,
				isError: isErrorInsightUsage,
			}}
			behaviouralUsageRequest={{
				data: behaviouralUsage,
				isFetching: isFetchingBehaviouralUsage,
				isError: isErrorBehaviouralUsage,
			}}
			feedbackRatingsRequest={{
				data: feedbackRatings,
				isFetching: isFetchingFeedbackRatings,
				isError: isErrorFeedbackRatings,
			}}
			userBehavioursRequest={{
				data: userBehaviours,
				isFetching: isFetchingUserBehaviours,
				isError: isErrorUserBehaviours,
			}}
			averageDocumentsRequest={{
				data: averageDocs,
				isFetching: isFetchingAverageDocs,
				isError: isErrorAverageDocs,
			}}
			creditDashboardRequest={{
				data: creditDashboard,
				isLoading: isFetchingCreditDashboard,
				isError: isErrorCreditDashboard,
			}}
			callbackRequestsRequest={{
				data: callbackReporting,
				isFetching: isFetchingCallbackReporting,
				isError: isErrorCallbackReporting,
			}}
			showExtendedCallbackStatuses={false}
			onExportFailure={() => toast.error(t('creditDashboard.exportFailed'))}
			exportQA={{download, data: {isLoading: isDownloadingData}}}
		/>
	);
};

export default Home;
